import type { Locale } from '../index'

/**
 * Track-by-reference page copy in all three locales (en / zh-Hant / zh-Hans).
 *
 * zh-Hant (Traditional — Taiwan / HK) and zh-Hans (Simplified — Mainland) are
 * distinct, hand-written translations — never auto-converted from one to the
 * other. Reference codes and tracking IDs stay as literals in the page; only
 * human-readable labels and state wording are keyed here.
 */
export const track: Record<Locale, Record<string, string>> = {
  en: {
    'track.meta.title': 'Track a shipment — Geo4Logistics',
    'track.meta.description':
      'Track your Geo4Logistics shipment by B/L, container or reference number for live, leg-by-leg status.',

    'track.head.title': 'Track a shipment',
    'track.head.subtitle':
      'Enter a B/L, container or Geo4 reference number for live, leg-by-leg status.',
    'track.input.placeholder': 'e.g. G4-43802470 · SHASF4001298A · SKYU3862450',
    'track.input.aria': 'Shipment reference',
    'track.button.track': 'Track',

    'track.empty.title': 'Enter a reference above',
    'track.empty.body':
      'Use a B/L, container, PO or Geo4 reference number — results come from your customer account.',
    'track.loading': 'Searching…',

    'track.type.shipment': 'Shipment',
    'track.type.quote': 'Quote',
    'track.type.invoice': 'Invoice',
    'track.results.hint': 'Results open in the customer portal.',

    'track.signin.title': 'Sign in to track your shipments',
    'track.signin.body':
      'Live tracking is available to Geo4 customers. Sign in to search your shipments, quotes and invoices — or get in touch and we’ll help.',
    'track.signin.login': 'Customer login',
    'track.signin.quote': 'Request a quote',
    'track.signin.contact': 'Contact us',

    'track.notfound.title': 'No shipment found',
    'track.notfound.body':
      "We couldn't match that reference. Double-check the number, or contact our team and we'll track it down.",

    'track.error.title': 'Something went wrong',
    'track.error.body': "We couldn't reach tracking just now. Please try again in a moment.",

    'track.portal.title': 'More in the customer portal',
    'track.portal.body.lead':
      'Documents, chat with your coordinator and full shipment history are in the customer portal.',
    'track.portal.link': 'Open the portal →',

    'track.help.title': "Can't find your shipment?",
    'track.help.body.lead': 'Check the reference, or',
    'track.help.body.link': 'contact our team',
    'track.help.body.tail':
      "— we're happy to look it up for you. You can also use the chat button in the corner.",
  },
  'zh-Hant': {
    'track.meta.title': '貨態查詢 — Geo4Logistics',
    'track.meta.description':
      '輸入提單、貨櫃或查詢編號，即時追蹤您的 Geo4Logistics 貨件每一段運程的狀態。',

    'track.head.title': '貨態查詢',
    'track.head.subtitle': '輸入提單、貨櫃或 Geo4 查詢編號，即時查看每一段運程的狀態。',
    'track.input.placeholder': '例如 G4-43802470 · SHASF4001298A · SKYU3862450',
    'track.input.aria': '貨件查詢編號',
    'track.button.track': '查詢',

    'track.empty.title': '請於上方輸入查詢編號',
    'track.empty.body': '輸入提單、貨櫃、PO 或 Geo4 查詢編號 — 查詢結果來自您的客戶帳戶。',
    'track.loading': '查詢中…',

    'track.type.shipment': '貨件',
    'track.type.quote': '報價',
    'track.type.invoice': '發票',
    'track.results.hint': '點選結果將於客戶平台開啟。',

    'track.signin.title': '登入以查詢您的貨件',
    'track.signin.body':
      '即時貨態查詢僅提供給 Geo4 客戶。登入後即可查詢您的貨件、報價與發票；或與我們聯絡，我們樂意協助。',
    'track.signin.login': '客戶登入',
    'track.signin.quote': '索取報價',
    'track.signin.contact': '聯絡我們',

    'track.notfound.title': '查無此貨件',
    'track.notfound.body': '我們找不到符合該編號的貨件。請再次確認編號，或聯絡我們的團隊代為查詢。',

    'track.error.title': '發生錯誤',
    'track.error.body': '目前無法查詢貨態，請稍後再試。',

    'track.portal.title': '更多功能盡在客戶平台',
    'track.portal.body.lead': '文件、與專屬協調員對話以及完整的貨件歷史紀錄，皆在客戶平台中提供。',
    'track.portal.link': '前往客戶平台 →',

    'track.help.title': '找不到您的貨件嗎？',
    'track.help.body.lead': '請確認查詢編號，或',
    'track.help.body.link': '聯絡我們的團隊',
    'track.help.body.tail': '— 我們樂意為您查詢。您也可以使用右下角的對話按鈕。',
  },
  'zh-Hans': {
    'track.meta.title': '货物追踪 — Geo4Logistics',
    'track.meta.description':
      '输入提单、集装箱或查询编号，即时追踪您的 Geo4Logistics 货件每一段运程的状态。',

    'track.head.title': '货物追踪',
    'track.head.subtitle': '输入提单、集装箱或 Geo4 查询编号，即时查看每一段运程的状态。',
    'track.input.placeholder': '例如 G4-43802470 · SHASF4001298A · SKYU3862450',
    'track.input.aria': '货件查询编号',
    'track.button.track': '追踪',

    'track.empty.title': '请在上方输入查询编号',
    'track.empty.body': '输入提单、集装箱、PO 或 Geo4 查询编号 — 查询结果来自您的客户账户。',
    'track.loading': '查询中…',

    'track.type.shipment': '货件',
    'track.type.quote': '报价',
    'track.type.invoice': '发票',
    'track.results.hint': '点击结果将在客户平台中打开。',

    'track.signin.title': '登录以追踪您的货件',
    'track.signin.body':
      '实时货物追踪仅面向 Geo4 客户。登录后即可查询您的货件、报价与发票；或与我们联系，我们乐意协助。',
    'track.signin.login': '客户登录',
    'track.signin.quote': '索取报价',
    'track.signin.contact': '联系我们',

    'track.notfound.title': '未找到货件',
    'track.notfound.body':
      '我们找不到与该编号匹配的货件。请再次核对编号，或联系我们的团队代为查询。',

    'track.error.title': '发生错误',
    'track.error.body': '目前无法查询货态，请稍后再试。',

    'track.portal.title': '更多功能尽在客户平台',
    'track.portal.body.lead': '文件、与专属协调员对话以及完整的货件历史记录，均在客户平台中提供。',
    'track.portal.link': '前往客户平台 →',

    'track.help.title': '找不到您的货件吗？',
    'track.help.body.lead': '请核对查询编号，或',
    'track.help.body.link': '联系我们的团队',
    'track.help.body.tail': '— 我们乐意为您查询。您也可以使用右下角的对话按钮。',
  },
}
